/**
 * Shared constants for the emotion-analysis demo.
 *
 * The backend emotion model emits 7 expression probabilities. We keep the
 * model's own palette here (literal hexes ported from the design mock) since
 * these are emotion-series colors, not theme tokens. Human-readable LABELS are
 * NOT here — they come from i18n (`liveDemo:emotions.*`).
 */

export type EmotionKey =
  | 'happy'
  | 'surprised'
  | 'neutral'
  | 'sad'
  | 'angry'
  | 'disgusted'
  | 'fearful'

export type Expressions = Record<EmotionKey, number>

/**
 * Color per expression. Picked for maximal mutual distinctness on a white
 * line chart (no two blues / no two reds — the old palette had both, which
 * made sad/fearful and happy/angry indistinguishable). Hues follow the
 * conventional emotion–color mapping (the Inside Out / affect-research one):
 * joy = warm amber, sadness = blue, anger = red, fear = purple,
 * disgust = green, surprise = teal, neutral = gray.
 */
export const FA_COLOR: Record<EmotionKey, string> = {
  happy: '#F5A623',
  surprised: '#0FB5C9',
  neutral: '#9AA0A6',
  sad: '#3B6FE0',
  angry: '#E03131',
  disgusted: '#2F9E44',
  fearful: '#8B3FD4',
}

/** Display order in bars / legend / timeline. */
export const FA_ORDER: EmotionKey[] = [
  'happy',
  'surprised',
  'neutral',
  'sad',
  'angry',
  'disgusted',
  'fearful',
]

export type Sample = {
  t: number
  e: Expressions
  /** Detected face box [x, y, w, h] in capture-frame pixels (for playback overlay). */
  face?: [number, number, number, number] | null
  /** Pixel size of the capture frame the `face` box refers to. */
  frame?: { w: number; h: number }
  /** Dominant-emotion confidence at this sample (for the playback HUD). */
  score?: number
  /**
   * True when the frames right before this sample had no usable detection (no
   * face, or the request failed). Timelines start a new subpath here so the
   * dropout stays a hole instead of a line drawn across it.
   */
  gap?: boolean
}

/** Dark video-stage background (literal, per task: HUD/media canvas area). */
export const STAGE_BG = '#0B0C1E'

export function fmtTime(s: number): string {
  if (!s || s < 0) return '0:00'
  const m = Math.floor(s / 60)
  const ss = Math.floor(s % 60)
  return `${m}:${String(ss).padStart(2, '0')}`
}

/** Highest-probability expression as [key, value], or null. */
export function dominant(e: Expressions | null): [EmotionKey, number] | null {
  if (!e) return null
  let best: [EmotionKey, number] = ['neutral', -1]
  for (const k of FA_ORDER) {
    const v = e[k] ?? 0
    if (v > best[1]) best = [k, v]
  }
  return best[1] < 0 ? null : best
}

/**
 * Interpolate the emotion vector at `currentMs` by linear interpolation between
 * the two neighbouring grid points (clamped to the ends). `series` values may be
 * sparse (null gaps) — those count as 0. Returns null when the grid is empty so
 * callers can render an inactive state.
 */
export function vectorAt(
  series: Record<EmotionKey, (number | null)[]>,
  gridMs: number[],
  currentMs: number,
): Expressions | null {
  const n = gridMs.length
  if (n === 0) return null

  const sampleAt = (i: number): Expressions => {
    const out: Expressions = { happy: 0, surprised: 0, neutral: 0, sad: 0, angry: 0, disgusted: 0, fearful: 0 }
    for (const k of FA_ORDER) out[k] = series[k]?.[i] ?? 0
    return out
  }

  // Clamp to ends.
  if (currentMs <= gridMs[0]) return sampleAt(0)
  if (currentMs >= gridMs[n - 1]) return sampleAt(n - 1)

  // Find the segment [lo, hi] containing currentMs.
  let hi = 1
  while (hi < n && gridMs[hi] < currentMs) hi += 1
  const lo = hi - 1
  const span = gridMs[hi] - gridMs[lo]
  const f = span > 0 ? (currentMs - gridMs[lo]) / span : 0
  const a = sampleAt(lo)
  const b = sampleAt(hi)

  const out: Expressions = { happy: 0, surprised: 0, neutral: 0, sad: 0, angry: 0, disgusted: 0, fearful: 0 }
  for (const k of FA_ORDER) out[k] = a[k] + (b[k] - a[k]) * f
  return out
}

/**
 * Map the backend emotion list (`[{ emotion, confidence }]`, same 7 labels as
 * our EmotionKey set) into the dense Expressions record the UI renders.
 */
export function toExpressions(
  list: Array<{ emotion: string; confidence: number }> | null | undefined,
): Expressions {
  const base: Expressions = { happy: 0, surprised: 0, neutral: 0, sad: 0, angry: 0, disgusted: 0, fearful: 0 }
  if (!list) return base
  for (const item of list) {
    if (item && (item.emotion as EmotionKey) in base) base[item.emotion as EmotionKey] = item.confidence ?? 0
  }
  return base
}

/**
 * Pixel size of the JPEG `captureJpeg` produces for this source (same
 * downscale math). Lets callers map backend face coordinates — which are in
 * the uploaded frame's pixel space — back onto the displayed media. Returns
 * null if the source has no pixels yet.
 */
export function captureSize(
  source: HTMLVideoElement | HTMLImageElement,
  maxDim = 480,
): { w: number; h: number } | null {
  const w = source instanceof HTMLVideoElement ? source.videoWidth : source.naturalWidth
  const h = source instanceof HTMLVideoElement ? source.videoHeight : source.naturalHeight
  if (!w || !h) return null
  const scale = Math.min(1, maxDim / Math.max(w, h))
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) }
}

/**
 * Snapshot a <video> or <img> to a JPEG Blob (downscaled to `maxDim`) for
 * upload to the backend emotion model. Returns null if the source has no pixels
 * yet.
 */
export function captureJpeg(
  source: HTMLVideoElement | HTMLImageElement,
  maxDim = 480,
  quality = 0.8,
): Promise<Blob | null> {
  const size = captureSize(source, maxDim)
  if (!size) return Promise.resolve(null)
  const canvas = document.createElement('canvas')
  canvas.width = size.w
  canvas.height = size.h
  const ctx = canvas.getContext('2d')
  if (!ctx) return Promise.resolve(null)
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', quality))
}
